import React, { useState } from 'react'
import { formatReadableDate } from '../../../util/string'
import ReadinessCheckListItem from './ReadinessCheckListItem'
import { DetailedProgramItem, ReadinessChecklist } from '../../../apis/types'
import Button from '../../../components/Form/Button'
import Modal from '../../../components/Modal'
import { useAppSelector, useAppDispatch } from '../../../shared/redux/hooks'
import { enrollSmdApi } from '../../../apis/apis'
import { pushToast } from '../../../components/Toaster/Toaster.slice'

const IntervalDataChecklistItem: React.FC<{
    program: DetailedProgramItem | undefined,
    readinessChecklist: ReadinessChecklist,
    handleRefreshReadinessChecklist: () => void
}> = ({
    program,
    readinessChecklist,
    handleRefreshReadinessChecklist
}) => {
    const dispatch = useAppDispatch()
    const { permissions } = useAppSelector((state) => state.app)
    const hasCapturePermission = permissions?.includes('program:manager:smd:enroll') ?? false
    const [confirmModal, setConfirmModal] = useState<React.ReactElement | boolean>(false)
    const [isSmdActionLoading, setIsSmdActionLoading] = useState(false)

    const handleEnrollSmd = async (): Promise<void> => {
        if (program?.id == null) return
        setIsSmdActionLoading(true)
        try {
            await new Promise((resolve, reject) => {
                setConfirmModal(<Modal resolve={resolve} reject={reject} />)
            })
        } catch (e) {
            // User clicked "No" on confirmation modal
            setConfirmModal(false)
            setIsSmdActionLoading(false)
            return
        }

        setConfirmModal(false)
        try {
            const response = await enrollSmdApi(program.id)
            if (response.success) {
                await dispatch(
                    pushToast({
                        type: 'success',
                        message: 'SMD enrollment job started successfully',
                        description: response.message,
                    })
                )
                handleRefreshReadinessChecklist()
            } else {
                throw new Error(response.error ?? response.message)
            }
        } catch (e: any) {
            await dispatch(
                pushToast({
                    type: 'error',
                    message: 'Error enrolling in SMD',
                    description: `${e.response?.data?.error ?? e.response?.data?.message ?? e.message ?? 'Unknown error'}`,
                })
            )
        } finally {
            setIsSmdActionLoading(false)
            setConfirmModal(false)
        }
    }

    // Not sure if we need to support job cancellation since we're not polling for job status
    // and we're not storing job status in the state
    
    // const handleCancelSmd = async (): Promise<void> => {
    //     if (program?.id == null) return
    //     setIsSmdActionLoading(true)
    //     try {
    //         await new Promise((resolve, reject) => {
    //             setConfirmModal(<Modal resolve={resolve} reject={reject} />)
    //         })
    //     } catch (e) {
    //         // User clicked "No" on confirmation modal
    //         setConfirmModal(false)
    //         setIsSmdActionLoading(false)
    //         return
    //     }

    //     setConfirmModal(false)
    //     try {
    //         const response = await cancelSmdApi(program.id)
    //         if (response.success) {
    //             await dispatch(
    //                 pushToast({
    //                     type: 'success',
    //                     message: 'SMD cancellation job started successfully',
    //                     description: response.message,
    //                 })
    //             )
    //             handleRefreshReadinessChecklist()
    //         } else {
    //             throw new Error(response.error ?? response.message)
    //         }
    //     } catch (e: any) {
    //         await dispatch(
    //             pushToast({
    //                 type: 'error',
    //                 message: 'Error cancelling SMD',
    //                 description: `${e.response?.data?.error ?? e.response?.data?.message ?? e.message ?? 'Unknown error'}`,
    //             })
    //         )
    //     } finally {
    //         setIsSmdActionLoading(false)
    //         setConfirmModal(false)
    //     }
    // }

    const isComplete = !readinessChecklist.portalLinking.isLinked;
    const isActive = ['created', 'pending', 'running'].includes(readinessChecklist.intervalDataStatus?.latestJob?.status ?? '');

    const infoComponent = [];
    const actionComponent = [];
    let showCaptureButton = false;

    if (readinessChecklist.intervalDataStatus == null || !readinessChecklist.portalLinking.isLinked) {
        return (
            <ReadinessCheckListItem
                isComplete={false}
                isActive={false}
                isIncomplete={true}
                headingText="Interval Data"
                infoComponent={<p>Program must first be linked to {program?.utility != null ? program?.utility : 'Utility'} portal to capture interval data.</p>}
            />
        )
    }

    if (program?.utility === 'SDG&E') {
        if (readinessChecklist?.intervalDataStatus?.lastBillingCycle?.startDate && readinessChecklist?.intervalDataStatus?.lastBillingCycle?.endDate) {
            infoComponent.push(
                <p>
                Latest billing cycle: {formatReadableDate(readinessChecklist?.intervalDataStatus?.lastBillingCycle.startDate, true)} - {formatReadableDate(readinessChecklist.intervalDataStatus.lastBillingCycle.endDate, true)}
                </p>
            )
        }
        if (readinessChecklist?.intervalDataStatus?.sdgeStatus?.status === 'VNEM_NOT_APPLIED') {
            infoComponent.push(
                <p>Capture not available – VNEM credits haven't been applied to latest billing cycle.</p>
            )
        }
        if (readinessChecklist?.intervalDataStatus?.sdgeStatus?.status === 'INTERVAL_DATA_EXISTS') {
            infoComponent.push(
                <p>Capture not needed – Interval data exists for latest billing cycle.</p>
            )
        }
        if (readinessChecklist?.intervalDataStatus?.sdgeStatus?.status === 'INTERVAL_DATA_MISSING') {
            infoComponent.push(
                <p>Capture recommended – Interval data missing for latest billing cycle.</p>
            )
            showCaptureButton = true;
        }
    } else {
        showCaptureButton = true;
        const intervalDataStatus = readinessChecklist.intervalDataStatus
        const dataRange = intervalDataStatus?.dataRange
        const shareMyData = intervalDataStatus?.shareMyData
        // Display latest billing cycle for PGE/SCE (same as SDGE)
        if (intervalDataStatus?.lastBillingCycle?.startDate && intervalDataStatus?.lastBillingCycle?.endDate) {
            infoComponent.push(
                <p key="billing-cycle">
                Latest billing cycle: {formatReadableDate(intervalDataStatus.lastBillingCycle.startDate, true)} - {formatReadableDate(intervalDataStatus.lastBillingCycle.endDate, true)}
                </p>
            )
        }
        if (dataRange?.minDate != null && dataRange?.maxDate != null) {
            infoComponent.push(
              <p key="data-range">Interval data range: {formatReadableDate(dataRange.minDate, true)} - {formatReadableDate(dataRange.maxDate, true)}</p>
            )
        }
        if (shareMyData?.lastEnrollmentDate) {
            infoComponent.push(
              <p key="last-enrollment">Last SMD authorization date: {formatReadableDate(shareMyData.lastEnrollmentDate, true)}</p>
            )
        }
        if (dataRange?.minDate == null || dataRange?.maxDate == null) {
            infoComponent.push(
                <p key="no-data">No interval data found.</p>
            )
            showCaptureButton = true;
        }
    }


    const isJobRunning = ['created', 'pending', 'running'].includes(readinessChecklist?.intervalDataStatus?.latestJob?.status ?? '');
    const canPullIntervalData = readinessChecklist?.intervalDataStatus?.canPullIntervalData ?? true;
    const blockedReasons = readinessChecklist?.intervalDataStatus?.blockedReasons ?? [];
    const isButtonDisabled = isJobRunning || isSmdActionLoading || !canPullIntervalData;

    // Display blocked reasons if pull is not allowed
    if (!canPullIntervalData && blockedReasons.length > 0) {
        infoComponent.push(
            <p key="blocked-reasons" className="text-red-600 dark:text-red-400">
                Cannot pull interval data: {blockedReasons.join(', ')}
            </p>
        )
    }

    actionComponent.push(
        <Button
            key="action-button"
            color="red"
            onClick={() => void handleEnrollSmd()}
            disabled={isButtonDisabled}
        >
            {isJobRunning || isSmdActionLoading
                ? program?.utility === 'SDG&E' ? 'Capturing Interval Data' : 'Refreshing SMD Authorization'
                : program?.utility === 'SDG&E' ? 'Capture Interval Data' : 'Refresh SMD Authorization'
            }
        </Button>
    )

    return (
        <>
            {confirmModal}
            <ReadinessCheckListItem
                isComplete={isComplete}
                isActive={isActive}
                isIncomplete={!isComplete && !isActive}
                headingText="Interval Data"
                infoComponent={<>{infoComponent}</>}
                actionComponent={<>{showCaptureButton && hasCapturePermission ? actionComponent : null}</>}
            />
        </>
    )
}

export default IntervalDataChecklistItem
